import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
	sortData,
	WsEntitySubscriptionService,
} from '@campaign-portal/components-library';

import {
	CreateResponse,
	DeleteRequest,
	DeleteResponse,
	ReadResponse,
} from '@campaign-portal/namespace/common/implementations';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { ContractCompany } from '@campaign-portal/namespace/entities/contract-companies/specs';
import { ContractCompanyEntity } from '../types';
import { WsResponseAction } from '@campaign-portal/namespace/common/ws';

@Injectable({
	providedIn: 'root',
})
export class ContractCompaniesService
	extends ExtendableRefBookService<ContractCompanyEntity<exist>>
	implements AbstractCRUDService
{
	readonly entityType = 'ContractCompanies';

	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService,
		private readonly wsEntity: WsEntitySubscriptionService
	) {
		super();
		this.subscribeToWsUpdates();
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.cc');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.cc');
	}

	override load(): Observable<ReadResponse<ContractCompanyEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<ContractCompany<exist>[]>>('ContractCompanies.Read', {}, this.loading$, this.errorNotifier)
			.pipe(
				map((resp) => {
					const entities = resp.Data.map((cc) => this.toEntity(cc));
					sortData(entities, 'name');
					return super.process({ ...resp, Data: entities });
				})
			);
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<ContractCompanyEntity<exist>[]>> {
		return this.api
			.loader<
				ReadResponse<ContractCompany<exist>[]>
			>('ContractCompanies.Read', params, this.loading$, this.errorNotifier)
			.pipe(map((resp) => ({ ...resp, Data: resp.Data.map((cc) => this.toEntity(cc)) })));
	}

	toEntity(cc: ContractCompany<exist>): ContractCompanyEntity<exist> {
		return new ContractCompanyEntity(cc);
	}

	update(company: ContractCompany): Observable<CreateResponse<ContractCompany>> {
		const notify = (response: CreateResponse<ContractCompany>): void => {
			if (response.Success) {
				const message = this.langService.translate(
					company.id ? 'notifications.actions.update' : 'notifications.actions.create',
					{
						entity: this.entity,
						name: company.name,
					}
				);
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<CreateResponse<ContractCompany>>(
			`ContractCompanies.${company.id ? 'Update' : 'Create'}`,
			{ Data: { Entities: [company] } },
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	delete(id: Id<exist>): Observable<DeleteResponse<ContractCompany>> {
		const params: DeleteRequest<ContractCompany<exist>> = {
			Data: {
				Ids: [id],
			},
		};
		const notify = (response: DeleteResponse<ContractCompany>): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<DeleteResponse<ContractCompany>>(
			'ContractCompanies.Delete',
			params,
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });

	private subscribeToWsUpdates(): void {
		this.wsEntity.subscribe<ContractCompany<exist>>(this.entityType).subscribe((resp) => {
			switch (resp.Action) {
				case WsResponseAction.CREATE: {
					const entities = (resp.Data?.Entities ?? []).map((partner) => this.toEntity(partner));
					const prepareListFn = (entities: ContractCompanyEntity<exist>[]): ContractCompanyEntity<exist>[] => {
						return sortData([...entities], 'name');
					};
					this.processWsCreate(entities, prepareListFn);
					break;
				}
				case WsResponseAction.UPDATE:
					{
						const entities = (resp.Data?.Entities ?? []).map((partner) => this.toEntity(partner));
						this.processWsUpdate(entities);
					}
					break;
				case WsResponseAction.DELETE:
					this.processWsDelete(resp.Data?.Ids);
					break;
			}
		});
	}
}
